import React, { useEffect } from 'react';
// Internal imports
import instructionsIcon from '~/images/icons/icons8-user_manual.png';
import AddDocumentModal from '~/app/Pages/Returns/components/Document/ArchivedDocuments/Modals/AddDocumentModal.jsx';
import { useSetState } from '~/app/Utility/customHooks';
// External imports
import {
  Divider,
  Input,
  FormControl,
  FormHelperText,
  withStyles,
  Button,
  Typography,
  Grid,
  FormControlLabel,
  Radio,
  RadioGroup,
  LinearProgress,
  CircularProgress,
} from '@material-ui/core';
// Styling imports
import '~/app/Pages/Returns/components/Document/AttachDocumentModal/css/AttachDocumentModal.css'; // Reuse available styling
import { styles } from '~/app/Pages/Returns/components/Document/AttachDocumentModal/css/AttachDocumentModal.js'; // Reuse available styling

/**
 * This component renders the Agent Assist Modal, works similar to Attach Document Modal
 *
 * @component VisionAssistModal
 * @category FormViewer
 **/

const VisionAssistModal = props => {
  const { classes } = props;
  const [state, setState] = useSetState({
    image: null,
    imageName: '',
    imageDescription: '',
    imageType: props.TVAargType,
    isPDF: false,
    isAddDocumentModalOpen: false,
    isRunningScan: false,
    buttonID: props.buttonID, // for use in case we want to know where the request orignated from, currently unused
    progressMessage: 'Upload Document     ',
  });

  useEffect(() => {
    let progressMessage;
    switch (props.TVAprogress) {
      case 25:
        progressMessage = 'Uploading Document...';
        break;
      case 50:
        progressMessage = 'Analyzing Document...';
        break;
      case 75:
        progressMessage = 'Writing Information to Return...';
        break;
      case 100:
        progressMessage = 'Scan Complete!';
        break;
      default:
        progressMessage = 'Upload Document...';
    }
    setState({
      progressMessage,
    });
  }, [props.TVAprogress]);

  const instructions = `Thank you for using Agent Assist.  
  When utilizing Agent Assist please make sure images are oriented correctly and that only one ID or form is included in uploaded images.  This will yield the best results.  
  Always double check import results and validate tax return information after Agent Assist document import.`;

  // Returns true if the upload type is a financial document (options 3, 5, 7, and 9) and the return is set to Married Filing Jointly (type 2)
  const shouldRenderSpouseUploadOption =
    props.TVAargType.match(/^[3579]$/) && String(props.clientFilingStatus) === '2';

  const setImage = imageItem => {
    setState({
      image: imageItem.image,
      imageName: imageItem.image_name,
      imageDescription: imageItem.image_description,
      isPDF: imageItem.is_pdf,
      attachmentType: imageItem.attachment_type,
    });
  };

  /**
   * For passing image to parent FormViewer where runVisionAssist scans image
   *
   */
  const handleScan = async e => {
    if (state.imageType === -1 || state.image === null) {
      props.showSnackBarErr('An image must be uploaded and a document type must be selected.');
      return;
    }

    await props.runTVASend(
      state.image,
      state.imageName,
      state.imageDescription.normalize('NFD').replace(/[\u0300-\u036f]/g, ''),
      state.imageType,
      state.isPDF,
    );

    await props.addOrAttachDocument(
      e,
      null,
      null,
      null,
      state.image,
      state.imageDescription.normalize('NFD').replace(/[\u0300-\u036f]/g, ''),
      state.imageName,
      state.imageType,
      state.attachmentType,
      null,
    );

    setState({
      isRunningScan: true,
    });
  };

  /** Handles opening/closing the add document modal */
  const toggleAddDocumentModal = () => {
    setState({ isAddDocumentModalOpen: !state.isAddDocumentModalOpen });
  };

  const getDocTypeLabel = value => {
    let docType = Number(props.TVAargType);
    if (value && value === 'Spouse') docType += 1;
    switch (docType) {
      case 1:
        return 'Taxpayer ID';
      case 2:
        return 'Spouse ID';
      case 3:
        return 'Taxpayer W-2';
      case 4:
        return 'Spouse W-2';
      case 5:
        return 'Taxpayer 1099K';
      case 6:
        return 'Spouse 1099K';
      case 7:
        return 'Taxpayer 1099R';
      case 8:
        return 'Spouse 1099R';
      case 9:
        return 'Taxpayer SSA1099';
      case 10:
        return 'Spouse SSA1099';
      default:
        return '';
    }
  };

  return (
    <>
      <AddDocumentModal
        showSnackBarErr={props.showSnackBarErr}
        isAddDocumentModalOpen={state.isAddDocumentModalOpen}
        toggleAddDocumentModal={toggleAddDocumentModal}
        isVisionAssist={true}
        setImage={setImage}
      />
      <Typography
        variant="body2"
        id="simple-modal-title"
        classes={{ root: classes.styledTypography }}
      >
        <span className="attach-doc-header">Agent Assist</span>
      </Typography>
      <div className="attach-doc-div-one">
        <div className="attach-doc-div-two">
          <Grid container>
            {state.isRunningScan && (
              <Grid
                container
                spacing={8}
                alignContent="flex-start"
                justify="flex-start"
                alignItems="baseline"
                className="tva-progress-bar"
              >
                <Grid container className="tva-progress-header">
                  <Grid item>
                    <Typography variant="body1">{state.progressMessage}</Typography>
                  </Grid>
                  {props.TVAprogress !== 100 && (
                    <Grid item className="tva-progress-wheel ">
                      <CircularProgress size={20} />
                    </Grid>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <LinearProgress variant="determinate" value={props.TVAprogress} />
                </Grid>
              </Grid>
            )}
            {!state.isRunningScan && (
              <Grid item xs={9} classes={{ item: classes.instructionsContainer }}>
                <div className="attach-doc-instructions-img-container">
                  <img
                    id="instructionsIcon"
                    className="attach-doc-instructions-img"
                    src={instructionsIcon}
                  />
                </div>
                <div className="attach-doc-instructions-text-container">
                  <Typography
                    classes={{ root: classes.instructionsTitle }}
                    variant="h6"
                    gutterBottom
                  >
                    Instructions:
                  </Typography>
                  <Typography classes={{ root: classes.instructions }} variant="body1">
                    {instructions}
                  </Typography>
                </div>
              </Grid>
            )}

            {!state.isRunningScan && (
              <>
                <Grid item xs={9} className={classes.styledFlexGrid}>
                  <Typography classes={{ root: classes.docTitle }} variant="h6" gutterBottom>
                    Attach Image
                  </Typography>
                </Grid>
                <Grid item xs={3} className={classes.styledFlexGrid}>
                  <Grid container justify="flex-end">
                    <Button
                      id="btnOpenAttachModalDocArchive"
                      size="medium"
                      color="primary"
                      classes={{ root: classes.addDocumentBtn }}
                      onClick={toggleAddDocumentModal}
                    >
                      Add Image
                    </Button>
                  </Grid>
                </Grid>
                <Grid item xs={12} className={classes.styledGridWithPadding}>
                  <Grid container justify="center">
                    <FormControl
                      fullWidth={true}
                      classes={{ root: classes.styledFormControlMarginBottom }}
                    >
                      <FormControl classes={{ root: classes.styledFormControl }}>
                        <Input
                          disableUnderline
                          disabled
                          placeholder="Upload Image"
                          classes={{ root: classes.styledInput }}
                          value={state.imageName}
                        />
                      </FormControl>
                      <FormHelperText>
                        Only Image files can be scanned via Agent Assist
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid
                  item
                  xs={12}
                  classes={{
                    item: classes.styledFlexGridDivider,
                  }}
                >
                  <Grid container justify="flex-start">
                    <Divider
                      classes={{
                        root: classes.documentDivider,
                      }}
                      variant="fullWidth"
                      light={true}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={9} className={classes.styledFlexGrid}>
                  <Typography classes={{ root: classes.docTitle }} variant="h6" gutterBottom>
                    Document Type
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  container
                  spacing={8}
                  alignContent="flex-start"
                  justify="flex-start"
                  alignItems="baseline"
                >
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      value={state.imageType}
                      onChange={e => {
                        setState({ imageType: e.target.value });
                      }}
                    >
                      {/* FormControlLabels would be better created in a function but they do not work correctly when doing so */}
                      <FormControlLabel
                        value={props.TVAargType}
                        control={<Radio />}
                        label={getDocTypeLabel('Main')}
                      />
                      {shouldRenderSpouseUploadOption && (
                        <FormControlLabel
                          value={String(Number(props.TVAargType) + 1)}
                          control={<Radio />}
                          label={getDocTypeLabel('Spouse')}
                        />
                      )}
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid
                  item
                  xs={12}
                  classes={{
                    item: classes.styledFlexGridDivider,
                  }}
                >
                  <Grid container justify="flex-start">
                    <Divider
                      classes={{
                        root: classes.documentDivider,
                      }}
                      variant="fullWidth"
                      light={true}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12} classes={{ item: classes.styledGridWithMargin }}>
                  <Grid container justify="flex-end">
                    <div className="attach-doc-div-four">
                      <Button
                        classes={{ root: classes.styleButtonMarginRight }}
                        size="small"
                        color="default"
                        onClick={props.handleCloseVisionModal}
                      >
                        Cancel
                      </Button>
                      <Button
                        id="btnAttachAddDocModal"
                        size="small"
                        color="primary"
                        variant="contained"
                        onClick={handleScan}
                      >
                        Scan
                      </Button>
                    </div>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </div>
      </div>
    </>
  );
};

export default withStyles(styles)(VisionAssistModal);
